import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, ArrowLeft, Check, Plus, RefreshCw, ShieldCheck, Trash2, Unplug, X } from "lucide-react";
import { toast } from "sonner";
import {
  bankConnect,
  bankDisconnect,
  bankResetDemo,
  bankSync,
  getBankBalances,
  getBankInstitutions,
  getBankStatus,
  getPaypalStatus,
  paypalDevSeed,
  paypalDisconnect,
  paypalSync,
  setAccountVisibility,
} from "@/lib/api";
import { getRedirectBase, openAuthUrl } from "@/lib/native";
import { BankLiquidityBreakdown } from "./bank/BankLiquidityBreakdown";
import { InstitutionPicker } from "./bank/InstitutionPicker";
import { PayPalBlock } from "./bank/PayPalBlock";
import { BankLogo, fmtSync } from "./bank/shared";

export const BankSheet = ({ open, onClose, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [institutions, setInstitutions] = useState(null);
  const [country, setCountry] = useState("IT");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("overview"); // overview | add
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);

  const [paypal, setPaypal] = useState(null);
  const [ppBusy, setPpBusy] = useState(null); // 'sync' | 'seed' | 'disconnect'

  const [balances, setBalances] = useState(null);
  const [showZero, setShowZero] = useState(false);
  const [accBusy, setAccBusy] = useState(null); // key del conto in aggiornamento

  const [confirmReset, setConfirmReset] = useState(false); // conferma a due passi
  const [resettingDemo, setResettingDemo] = useState(false);

  const loadBalances = useCallback(async () => {
    try {
      setBalances(await getBankBalances());
    } catch {
      setBalances(null);
    }
  }, []);

  const loadPaypal = useCallback(async () => {
    try {
      setPaypal(await getPaypalStatus());
    } catch {
      setPaypal({ configured: false, connected: false });
    }
  }, []);

  const loadStatus = useCallback(async () => {
    const s = await getBankStatus();
    setStatus(s);
    return s;
  }, []);

  const loadInstitutions = useCallback(async (c) => {
    setInstitutions(null);
    try {
      const inst = await getBankInstitutions(c);
      setInstitutions(inst.institutions || []);
    } catch {
      setInstitutions([]);
      toast.error("Impossibile caricare l'elenco banche");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setStatus(null);
      setInstitutions(null);
      setQuery("");
      setCountry("IT");
      setPaypal(null);
      setBalances(null);
      setShowZero(false);
      setConfirmReset(false);
      loadPaypal();
      loadBalances();
      loadStatus()
        .then((s) => {
          if (!s.configured || !s.connected) setMode("add");
          else setMode("overview");
        })
        .catch(() => toast.error("Impossibile leggere lo stato della banca"));
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
    // loadPaypal/loadBalances sono useCallback stabili: rieseguiamo solo all'apertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadStatus]);

  useEffect(() => {
    if (open && mode === "add" && status?.configured) {
      loadInstitutions(country);
    }
  }, [open, mode, country, status?.configured, loadInstitutions]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!institutions) return [];
    const q = query.trim().toLowerCase();
    if (!q) return institutions;
    return institutions.filter((i) => (i.name || "").toLowerCase().includes(q));
  }, [institutions, query]);

  const handleSelect = async (inst) => {
    setConnecting(inst.id);
    try {
      const res = await bankConnect({
        institution_id: inst.id,
        redirect_origin: getRedirectBase(),
        institution_name: inst.name,
        institution_logo: inst.logo || "",
        country: inst.country || country,
      });
      await openAuthUrl(res.link);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connessione alla banca fallita");
      setConnecting(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await bankSync();
      const n = res.imported || 0;
      if (n > 0) toast.success(`${n} ${n === 1 ? "nuova transazione importata" : "nuove transazioni importate"} · analisi in corso`);
      else toast("Sei già aggiornato — nessuna nuova transazione");
      await loadStatus();
      await loadBalances();
      onChanged?.();
      if (n > 0) {
        setTimeout(() => { loadBalances(); onChanged?.(); }, 12000);
        setTimeout(() => { loadBalances(); onChanged?.(); }, 30000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync non riuscito");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    setDisconnecting(connectionId);
    try {
      await bankDisconnect(connectionId);
      toast("Banca scollegata");
      const s = await loadStatus();
      if (!s.connected) setMode("add");
      onChanged?.();
    } catch {
      toast.error("Impossibile scollegare la banca");
    } finally {
      setDisconnecting(null);
    }
  };

  const connections = status?.connections || [];

  const handleResetDemo = async () => {
    setResettingDemo(true);
    try {
      const res = await bankResetDemo();
      const n = res.removed_transactions || 0;
      toast.success(
        n > 0
          ? `Dati demo rimossi (${n} movimenti). Ora puoi collegare la tua banca reale.`
          : "Dati demo rimossi. Ora puoi collegare la tua banca reale."
      );
      setConfirmReset(false);
      const s = await loadStatus();
      await loadBalances();
      if (!s.connected) setMode("add");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile rimuovere i dati demo");
    } finally {
      setResettingDemo(false);
    }
  };

  const toggleAccount = async (acc) => {
    setAccBusy(acc.key);
    try {
      const res = await setAccountVisibility(acc.key, !acc.hidden);
      setBalances(res);
      toast(acc.hidden ? "Conto incluso nel totale" : "Conto escluso dal totale");
      onChanged?.();
    } catch {
      toast.error("Impossibile aggiornare il conto");
    } finally {
      setAccBusy(null);
    }
  };

  const refreshPaypal = async () => {
    await loadPaypal();
    onChanged?.();
  };

  const handlePaypalSync = async () => {
    setPpBusy("sync");
    try {
      const res = await paypalSync();
      const n = res.imported || 0;
      if (n > 0) toast.success(`${n} ${n === 1 ? "movimento PayPal importato" : "movimenti PayPal importati"} · analisi in corso`);
      else toast("PayPal aggiornato — nessun nuovo movimento");
      await refreshPaypal();
      if (n > 0) {
        setTimeout(() => onChanged?.(), 12000);
        setTimeout(() => onChanged?.(), 30000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync PayPal non riuscito");
    } finally {
      setPpBusy(null);
    }
  };

  const handlePaypalSeed = async () => {
    setPpBusy("seed");
    try {
      const res = await paypalDevSeed();
      toast.success(`${res.imported || 0} movimenti PayPal di test importati`);
      await refreshPaypal();
    } catch {
      toast.error("Impossibile caricare i dati di test PayPal");
    } finally {
      setPpBusy(null);
    }
  };

  const handlePaypalDisconnect = async () => {
    setPpBusy("disconnect");
    try {
      await paypalDisconnect();
      toast("PayPal scollegato");
      await refreshPaypal();
    } catch {
      toast.error("Impossibile scollegare PayPal");
    } finally {
      setPpBusy(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="bank-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            {/* header terminale */}
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <div className="flex items-center gap-3">
                {mode === "add" && status?.connected && (
                  <button
                    onClick={() => setMode("overview")}
                    data-testid="bank-back-button"
                    aria-label="Indietro"
                    className="flex h-7 w-7 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
                  >
                    <ArrowLeft size={13} />
                  </button>
                )}
                <span className="mono text-sm font-medium tracking-[0.1em] text-[#00FF87]">
                  CONTI <span className="text-white/25">//</span> LIQO
                </span>
                {status?.sandbox && (
                  <span className="caps-tag rounded-[4px] border border-[#FFD60A]/40 px-1.5 py-0.5 text-[#FFD60A]/80">
                    SANDBOX
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                data-testid="bank-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {/* ---- Avviso dati DEMO rilevati ---- */}
              {status?.demo_data && (
                <div
                  className="mb-4 rounded-[12px] border border-[#FFD60A]/30 bg-[#FFD60A]/[0.04] p-4 shadow-[0_0_0_1px_rgba(255,214,10,0.08)]"
                  data-testid="bank-demo-warning"
                >
                  <div className="flex items-center gap-2 text-[#FFD60A]">
                    <AlertTriangle size={15} />
                    <p className="mono text-xs tracking-[0.1em]">DATI DEMO RILEVATI</p>
                  </div>
                  <p className="mt-3 text-[13px] font-light leading-6 text-white/60">
                    Nel tuo account sono presenti dati dimostrativi (es. "Banca Demo").
                    Puoi rimuoverli per partire pulito.
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-[8px] border border-[#00FF87]/20 bg-[#00FF87]/[0.04] px-3 py-2">
                    <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#00FF87]" />
                    <p className="text-[12px] font-light leading-5 text-white/55">
                      Questa azione rimuoverà <span className="text-white/80">solo i dati demo</span>:
                      i tuoi conti reali non saranno toccati.
                    </p>
                  </div>

                  {!confirmReset ? (
                    <button
                      onClick={() => setConfirmReset(true)}
                      data-testid="bank-reset-demo-button"
                      className="mono mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-[#FF4D4D]/40 bg-transparent text-[11px] font-medium uppercase tracking-[0.18em] text-[#FF4D4D]/90 hover:bg-[#FF4D4D]/[0.06] active:scale-[0.98]"
                    >
                      <Trash2 size={13} />
                      Rimuovi dati demo
                    </button>
                  ) : (
                    <div className="mt-3 space-y-2" data-testid="bank-reset-demo-confirm">
                      <p className="mono text-center text-[10px] tracking-[0.12em] text-white/45">
                        SEI SICURO? L'AZIONE È DEFINITIVA
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmReset(false)}
                          disabled={resettingDemo}
                          data-testid="bank-reset-demo-cancel"
                          className="mono flex h-11 flex-1 items-center justify-center rounded-[6px] border border-white/[0.1] bg-transparent text-[10px] font-medium uppercase tracking-[0.18em] text-white/50 hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-50"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={handleResetDemo}
                          disabled={resettingDemo}
                          data-testid="bank-reset-demo-confirm-button"
                          className="mono flex h-11 flex-[1.4] items-center justify-center gap-2 rounded-[6px] border border-[#FF4D4D]/50 bg-[#FF4D4D]/[0.08] text-[10px] font-medium uppercase tracking-[0.16em] text-[#FF4D4D] hover:bg-[#FF4D4D]/[0.14] active:scale-[0.98] disabled:opacity-50"
                        >
                          {resettingDemo ? (
                            <RefreshCw size={12} className="animate-spin" />
                          ) : (
                            <Trash2 size={12} />
                          )}
                          {resettingDemo ? "Rimuovo…" : "Conferma rimozione"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ---- Fonte PayPal ---- */}
              <PayPalBlock
                paypal={paypal}
                ppBusy={ppBusy}
                onSync={handlePaypalSync}
                onSeed={handlePaypalSeed}
                onDisconnect={handlePaypalDisconnect}
              />
              <div className="mb-4 h-px bg-white/[0.06]" />

              {/* loading */}
              {status === null && (
                <div className="space-y-3" data-testid="bank-loading">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-14 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  ))}
                </div>
              )}

              {/* non configurato */}
              {status && !status.configured && (
                <div
                  className="rounded-[12px] border border-[#FFD60A]/25 bg-[#0D0D14] p-4"
                  data-testid="bank-not-configured"
                >
                  <div className="flex items-center gap-2 text-[#FFD60A]">
                    <AlertTriangle size={15} />
                    <p className="mono text-xs tracking-[0.1em]">ENABLE BANKING NON CONFIGURATO</p>
                  </div>
                  <p className="mt-3 text-[13px] font-light leading-6 text-white/55">
                    L'integrazione Open Banking è pronta, ma mancano le credenziali API.
                    Registra un'app nel Control Panel di Enable Banking per ottenere Application ID
                    e la chiave privata, poi inseriscili per collegare le tue banche.
                  </p>
                </div>
              )}

              {/* OVERVIEW: banche collegate */}
              {status && status.configured && mode === "overview" && (
                <div data-testid="bank-connected-view">
                  {/* ---- Dettaglio liquidità per banca (deduplicato) ---- */}
                  <BankLiquidityBreakdown
                    balances={balances}
                    showZero={showZero}
                    setShowZero={setShowZero}
                    accBusy={accBusy}
                    onToggleAccount={toggleAccount}
                  />

                  <div className="space-y-3">
                    {connections.map((conn) => (
                      <div
                        key={conn.id}
                        className="rounded-[12px] border border-[#00FF87]/25 bg-[#0D0D14] p-4 shadow-[0_0_0_1px_rgba(0,255,135,0.1)]"
                        data-testid={`bank-connection-${conn.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <BankLogo inst={{ name: conn.institution_name, logo: conn.institution_logo }} size={9} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-light text-white/90" data-testid="bank-institution-name">
                              {conn.institution_name || "Banca collegata"}
                            </p>
                            <p className="caps-tag mt-0.5 text-[#00FF87]/70">
                              {conn.country} · collegata · sync {fmtSync(conn.accounts[0]?.last_sync_at || status.last_sync_at)}
                            </p>
                          </div>
                          <Check size={16} className="shrink-0 text-[#00FF87]" />
                        </div>

                        <button
                          onClick={() => handleDisconnect(conn.id)}
                          disabled={disconnecting === conn.id}
                          data-testid={`bank-disconnect-${conn.id}`}
                          className="mono mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.1] bg-transparent text-[10px] font-medium uppercase tracking-[0.18em] text-white/45 hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-50"
                        >
                          <Unplug size={12} />
                          {disconnecting === conn.id ? "Scollego…" : "Scollega"}
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* aggiungi banca */}
                  <button
                    onClick={() => {
                      setQuery("");
                      setMode("add");
                    }}
                    data-testid="bank-add-bank-button"
                    className="mono mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-[6px] border border-dashed border-white/15 bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-white/60 hover:border-[#00FF87]/40 hover:text-[#00FF87] active:scale-[0.98]"
                  >
                    <Plus size={14} />
                    Aggiungi un'altra banca
                  </button>

                  {/* sync globale */}
                  <button
                    onClick={handleSync}
                    disabled={syncing}
                    data-testid="bank-sync-now-button"
                    className="mono mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-[6px] border border-[#00FF87]/40 bg-transparent text-[11px] font-medium uppercase tracking-[0.22em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.1)] hover:bg-[#00FF87]/[0.06] active:scale-[0.98] disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                    {syncing ? "Sincronizzo…" : "Sync ora (tutte)"}
                  </button>

                  <p className="mono mt-3 px-1 text-center text-[10px] tracking-[0.08em] text-white/30">
                    Ultimo sync: {fmtSync(status.last_sync_at)} · auto ogni 6h
                  </p>
                  {status.last_error && (
                    <p className="mt-1 px-1 text-center text-[11px] font-light text-[#FF4D4D]/80" data-testid="bank-last-error">
                      {status.last_error}
                    </p>
                  )}
                </div>
              )}

              {/* ADD: selezione banca + paese */}
              {status && status.configured && mode === "add" && (
                <InstitutionPicker
                  status={status}
                  country={country}
                  setCountry={setCountry}
                  query={query}
                  setQuery={setQuery}
                  institutions={institutions}
                  filtered={filtered}
                  connecting={connecting}
                  onSelect={handleSelect}
                />
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
